import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from 'react-query'
import {
  alpha,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { AppStateContext } from '../../../providers/AppStateContext'
import { formatDate } from '../../../utils/formatting'
import { AppPermissions } from '../../../models/AppPermissions.model'
import { TuningProfileField } from '../../../models/TuningProfile.model'
import UserService from '../../../services/UserService/UserService'
import { TrailSystemService } from '../../../services/TrailSystemService/TrailSystemService'
import { TuningProfileService } from '../../../services/TuningProfileService/TuningProfileService'
import { ConfigService } from '../../../services/ConfigService/ConfigService'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { tuningProfileFieldLabels, tuningProfileTrailFields } from '../shared/TuningProfiles/tuningProfileFormatting'
import ApplyTuningProfileDialog from '../shared/TuningProfiles/ApplyTuningProfileDialog.component'
import {
  draftChanges,
  draftFromDetail,
  settingsUpdateFromDraft,
  SYSTEM_ROW,
  TuningProfileDraft,
  tuningProfileSystemFields,
  tuningValueError,
} from './tuningProfileEditor.helpers'

// height of the app's top bar on wider screens, which the page scrolls underneath
const APP_BAR_HEIGHT = 64

type TuningCellProps = {
  label: string
  value: string
  placeholder: string
  changed: boolean
  error?: string
  disabled: boolean
  onChange: (value: string) => void
}
// one editable value; changed values are highlighted and invalid ones show why on hover
const TuningCell = (props: TuningCellProps) => {
  const { label, value, placeholder, changed, error, disabled, onChange } = props
  return (
    <Tooltip title={error ?? ''} disableHoverListener={!error} disableFocusListener={!error} placement="top">
      <OutlinedInput
        size="small"
        fullWidth
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        error={!!error}
        onChange={(e) => onChange(e.target.value)}
        inputProps={{ 'aria-label': label, 'aria-invalid': !!error, inputMode: 'decimal' }}
        sx={[
          changed &&
            !error && {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
            },
        ]}
      />
    </Tooltip>
  )
}

/**
 * @description Edits every value of one tuning profile on a single page: the trail system's, then one
 * row per trail, with search and a bulk "set to" for ticked trails. Saving a profile that isn't active
 * changes only the profile; saving the active one also updates the live values.
 */
const TuningProfileEditor = (): JSX.Element => {
  const { id, profileId } = useParams<{ id: string; profileId: string }>()
  const trailSystemId = Number(id)
  const tuningProfileId = Number(profileId)

  const { user } = useAuth0()
  const canEdit = UserService.hasPermissions(user, [AppPermissions.editTrailSystem])
  const queryClient = useQueryClient()
  const tuningProfileService = useMemo(() => new TuningProfileService(), [])

  useEffect(() => {
    AppLayoutContext.setPageName('Tuning Profile')
  }, [])

  const { data: trailSystem } = useQuery(
    ['TrailSystemService.get', { id: trailSystemId, relations: [] }, AppStateContext.getRegion()],
    () => new TrailSystemService().get({ id: trailSystemId, relations: [] }),
  )
  const { data: regionConfig } = useQuery(['ConfigService.get'], () => new ConfigService().get())

  const detailQueryKey = ['TuningProfileService.getDetail', { id: tuningProfileId }, AppStateContext.getRegion()]
  const {
    data: detail,
    isLoading,
    error: loadingError,
  } = useQuery(detailQueryKey, () => tuningProfileService.getDetail(tuningProfileId), {
    // always start from the saved values, not an older copy
    cacheTime: 0,
  })

  const original = useMemo<TuningProfileDraft>(() => (detail ? draftFromDetail(detail) : {}), [detail])
  // only the edited copy is held in state; until something is typed (and again after a save or
  // discard) the page shows the saved values directly, so they're there on the first render
  const [editedDraft, setEditedDraft] = useState<TuningProfileDraft | null>(null)
  const draft = editedDraft ?? original
  const setDraft = (update: (prev: TuningProfileDraft) => TuningProfileDraft) =>
    setEditedDraft((prev) => update(prev ?? original))

  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [bulkField, setBulkField] = useState<TuningProfileField>('dryTimeModifier')
  const [bulkValue, setBulkValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const changes = useMemo(() => draftChanges(draft, original), [draft, original])
  const isChanged = (row: string, field: TuningProfileField) =>
    changes.some((change) => change.row === row && change.field === field)
  const errorCount = Object.keys(draft).reduce(
    (count, row) =>
      count +
      (Object.keys(draft[row]) as TuningProfileField[]).filter((field) => tuningValueError(field, draft[row][field]))
        .length,
    0,
  )

  // warn before leaving the page with unsaved edits
  useEffect(() => {
    if (!changes.length) {
      return
    }
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [changes.length])

  // what a blank trail value falls back to: the trail system's value as currently typed, else the
  // region's lookback or the default modifier of 1
  const systemDraft = draft[SYSTEM_ROW] ?? {}
  const inherited: Record<TuningProfileField, string> = {
    weatherResistance: '',
    precipitationLookbackDays:
      systemDraft.precipitationLookbackDays?.trim() || String(regionConfig?.precipitationLookbackDays || 7),
    saturationModifier: systemDraft.saturationModifier?.trim() || '1',
    dryTimeModifier: systemDraft.dryTimeModifier?.trim() || '1',
  }

  const setValue = (row: string, field: TuningProfileField, value: string) =>
    setDraft((prev) => ({ ...prev, [row]: { ...prev[row], [field]: value } }))

  const trails = detail?.trails ?? []
  const visibleTrails = trails.filter((trail) => trail.name.toLowerCase().includes(search.trim().toLowerCase()))
  const visibleIds = visibleTrails.map((trail) => String(trail.trailId))
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((trailId) => selected.includes(trailId))

  const setSelectedValues = (value: string) => {
    setDraft((prev) => {
      const next = { ...prev }
      selected.forEach((trailId) => {
        next[trailId] = { ...next[trailId], [bulkField]: value }
      })
      return next
    })
  }

  // the active profile's name, for the apply dialog's "Summer keeps its values" note
  const { data: profiles } = useQuery(
    ['TuningProfileService.list', { trailSystemId }, AppStateContext.getRegion()],
    () => tuningProfileService.list(trailSystemId),
    { enabled: canEdit && !!trailSystemId },
  )
  const activeProfileName = profiles?.find((profile) => profile.active)?.name

  const [isApplyOpen, setIsApplyOpen] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const handleApply = () => {
    if (!detail) {
      return
    }
    setIsApplying(true)
    tuningProfileService
      .apply(detail.profile.id)
      .then((applied) => {
        SnackbarContext.show(
          `${applied.name} applied. Statuses update at the next hourly refresh.`,
          'success',
          APP_UPDATE_NOTICE,
        )
        setIsApplyOpen(false)
        // the profile is now active and the live values changed, so refresh the page and everything else
        queryClient.invalidateQueries()
      })
      .catch((err) => {
        console.error(err)
        SnackbarContext.show(typeof err === 'string' ? err : 'The profile failed to apply. Please try again.', 'error')
      })
      .finally(() => setIsApplying(false))
  }

  const handleSave = () => {
    if (!detail || !changes.length || errorCount) {
      return
    }
    setIsSaving(true)
    tuningProfileService
      .updateSettings(tuningProfileId, settingsUpdateFromDraft(draft, changes))
      .then((saved) => {
        queryClient.setQueryData(detailQueryKey, saved)
        setEditedDraft(null)
        const count = `${changes.length} ${changes.length === 1 ? 'change' : 'changes'}`
        if (saved.profile.active) {
          SnackbarContext.show(
            `Saved ${count} to ${saved.profile.name} and updated the live values. Statuses update at the next hourly refresh.`,
            'success',
            APP_UPDATE_NOTICE,
          )
        } else {
          SnackbarContext.show(
            `Saved ${count} to ${saved.profile.name}. Apply it when you want the trails to use them.`,
            'success',
          )
        }
        // the saved profile is already on screen; refresh everything else that may show these values
        queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'TuningProfileService.getDetail' })
      })
      .catch((err) => {
        console.error(err)
        SnackbarContext.show(typeof err === 'string' ? err : 'The changes failed to save. Please try again.', 'error')
      })
      .finally(() => setIsSaving(false))
  }

  // the top section's height sets where the table's header row sticks; and once it's stuck it gets a
  // solid background so rows scroll out of sight beneath it
  const sentinelRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const [stickyHeight, setStickyHeight] = useState(0)
  const [isStuck, setIsStuck] = useState(false)
  useEffect(() => {
    const section = stickyRef.current
    const sentinel = sentinelRef.current
    if (!section || !sentinel || typeof ResizeObserver === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return
    }
    const resizeObserver = new ResizeObserver(() => setStickyHeight(section.offsetHeight))
    resizeObserver.observe(section)
    const intersectionObserver = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting && entry.boundingClientRect.top < APP_BAR_HEIGHT),
      { rootMargin: `-${APP_BAR_HEIGHT}px 0px 0px 0px` },
    )
    intersectionObserver.observe(sentinel)
    return () => {
      resizeObserver.disconnect()
      intersectionObserver.disconnect()
    }
  }, [])

  const profileName = detail?.profile.name ?? 'Tuning'
  const isActive = !!detail?.profile.active

  return (
    <Paper sx={{ p: 2 }}>
      {/* marks where the top section sits at rest, to tell when it has become stuck */}
      <Box ref={sentinelRef} />
      {/* on wider screens everything down to the table's column headers stays in view while the page
      scrolls: this section sticks under the app bar and the header row sticks under it */}
      <Box
        ref={stickyRef}
        sx={{
          position: { md: 'sticky' },
          top: { md: APP_BAR_HEIGHT },
          zIndex: 3,
          mx: -2,
          mt: -2,
          px: 2,
          pt: 2,
          pb: 2,
          ...(isStuck && { bgcolor: 'secondary.main', borderBottom: 1, borderColor: 'divider' }),
        }}
      >
        <Stack
          direction={{ zero: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Breadcrumbs
            items={[
              { label: 'Trail Systems', href: '/trail-systems' },
              { label: trailSystem?.name ?? 'Trail System', href: `/trail-systems/${trailSystemId}` },
              { label: `${profileName} Profile` },
            ]}
          />
          {canEdit && detail && (
            <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
              {!detail.profile.active && (
                // applies what's saved, so unsaved edits have to be saved or discarded first
                <Tooltip title={changes.length ? 'Save or discard your changes first' : ''}>
                  <span>
                    <Button
                      variant="outlined"
                      disabled={!!changes.length || isSaving || isApplying}
                      onClick={() => setIsApplyOpen(true)}
                    >
                      Apply Profile
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button variant="text" disabled={!changes.length || isSaving} onClick={() => setEditedDraft(null)}>
                Discard Changes
              </Button>
              <Button variant="contained" disabled={!changes.length || !!errorCount || isSaving} onClick={handleSave}>
                {changes.length ? `Save ${changes.length} ${changes.length === 1 ? 'Change' : 'Changes'}` : 'Save'}
              </Button>
            </Stack>
          )}
        </Stack>

        {detail && (
          <>
            <DetailHeader
              title={canEdit ? `Edit ${detail.profile.name}` : `${detail.profile.name} Profile`}
              badges={
                isActive ? (
                  <Chip size="small" color="primary" label="Active" />
                ) : (
                  <Chip size="small" variant="outlined" label="Inactive" />
                )
              }
              subtitle={`${trailSystem?.name ?? 'Trail System'} · System + ${detail.trails.length} ${
                detail.trails.length === 1 ? 'trail' : 'trails'
              }`}
              meta={[`Saved ${formatDate(detail.profile.updatedAt)}`]}
            />

            <Stack spacing={2}>
              <Alert severity="info">
                {isActive
                  ? `${detail.profile.name} is the active profile, so saving also updates the live values on these trails. Statuses pick them up at the next hourly refresh.`
                  : `${detail.profile.name} isn't active, so saving changes only this profile. The trails keep their current values until ${detail.profile.name} is applied.`}
              </Alert>

              <Stack direction={{ zero: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <TextField
                  size="small"
                  label="Search Trails"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ width: { md: 280 } }}
                />
                {canEdit && selected.length > 0 ? (
                  <Stack
                    role="group"
                    aria-label="Change selected trails"
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    gap={1.5}
                    sx={{
                      px: 1.5,
                      py: 1,
                      border: 1,
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {selected.length} {selected.length === 1 ? 'trail' : 'trails'} selected
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="bulk-field-label">Value</InputLabel>
                      <Select
                        labelId="bulk-field-label"
                        label="Value"
                        value={bulkField}
                        onChange={(e) => setBulkField(e.target.value as TuningProfileField)}
                      >
                        {tuningProfileTrailFields.map((field) => (
                          <MenuItem key={field} value={field}>
                            {tuningProfileFieldLabels[field]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Set To"
                      value={bulkValue}
                      onChange={(e) => setBulkValue(e.target.value)}
                      error={!!bulkValue && !!tuningValueError(bulkField, bulkValue)}
                      inputProps={{ inputMode: 'decimal' }}
                      sx={{ width: 110 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!bulkValue.trim() || !!tuningValueError(bulkField, bulkValue)}
                      onClick={() => setSelectedValues(bulkValue.trim())}
                    >
                      Set
                    </Button>
                    {bulkField !== 'weatherResistance' && (
                      <Button variant="outlined" size="small" onClick={() => setSelectedValues('')}>
                        Clear To System Value
                      </Button>
                    )}
                    <Button size="small" color="inherit" onClick={() => setSelected([])}>
                      Deselect
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="grey.500">
                    {canEdit
                      ? "Tick trails to set a value on all of them at once. Blank values use the trail system's value, shown in grey."
                      : "Blank values use the trail system's value, shown in grey."}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </>
        )}
      </Box>

      {isLoading && <Loading sx={{ py: 20 }} />}
      {!isLoading && !!loadingError && (
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
          <Typography variant="error">
            {typeof loadingError === 'string' ? loadingError : 'Failed to load the tuning profile.'}
          </Typography>
        </Stack>
      )}

      {detail && (
        <Stack spacing={2}>
          {/* one scroll area: the page. On wider screens the table doesn't scroll on its own, so its header
            row can stick under the top section; on phones it scrolls sideways inside its box instead */}
          <TableContainer
            sx={{
              overflowX: { zero: 'auto', md: 'visible' },
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                tableLayout: 'fixed',
                minWidth: 760,
                '& .MuiTableCell-stickyHeader': { top: { md: APP_BAR_HEIGHT + stickyHeight } },
              }}
            >
              {/* fixed layout takes its column widths from here; a width on the header cell alone let the
                checkbox column shrink below the checkbox, and the sticky Trail header covered the rest */}
              <colgroup>
                {canEdit && <col style={{ width: 64 }} />}
                <col style={{ width: '28%' }} />
                {tuningProfileTrailFields.map((field) => (
                  <col key={field} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  {canEdit && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={!allVisibleSelected && visibleIds.some((trailId) => selected.includes(trailId))}
                        onChange={() =>
                          setSelected((prev) =>
                            allVisibleSelected
                              ? prev.filter((trailId) => !visibleIds.includes(trailId))
                              : Array.from(new Set([...prev, ...visibleIds])),
                          )
                        }
                        inputProps={{ 'aria-label': 'Select all trails shown' }}
                      />
                    </TableCell>
                  )}
                  <TableCell>Trail</TableCell>
                  {tuningProfileTrailFields.map((field) => (
                    <TableCell key={field}>
                      {field === 'precipitationLookbackDays' ? 'Lookback (Days)' : tuningProfileFieldLabels[field]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: 'surface.section' }}>
                  {canEdit && <TableCell padding="checkbox" />}
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      Trail System
                    </Typography>
                    <Typography variant="caption" color="grey.500">
                      Used by trails left blank
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: 'grey.500' }}>—</TableCell>
                  {tuningProfileSystemFields.map((field) => (
                    <TableCell key={field}>
                      <TuningCell
                        label={`Trail System ${tuningProfileFieldLabels[field]}`}
                        value={systemDraft[field] ?? ''}
                        placeholder={
                          field === 'precipitationLookbackDays'
                            ? `Region (${regionConfig?.precipitationLookbackDays || 7})`
                            : 'Default (1)'
                        }
                        changed={isChanged(SYSTEM_ROW, field)}
                        error={tuningValueError(field, systemDraft[field])}
                        disabled={!canEdit || isSaving}
                        onChange={(value) => setValue(SYSTEM_ROW, field, value)}
                      />
                    </TableCell>
                  ))}
                </TableRow>

                {visibleTrails.map((trail) => {
                  const row = String(trail.trailId)
                  const isSelected = selected.includes(row)
                  return (
                    <TableRow key={row} selected={isSelected}>
                      {canEdit && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() =>
                              setSelected((prev) =>
                                isSelected ? prev.filter((trailId) => trailId !== row) : [...prev, row],
                              )
                            }
                            inputProps={{ 'aria-label': `Select ${trail.name}` }}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>{trail.name}</TableCell>
                      {tuningProfileTrailFields.map((field) => (
                        <TableCell key={field}>
                          <TuningCell
                            label={`${trail.name} ${tuningProfileFieldLabels[field]}`}
                            value={draft[row]?.[field] ?? ''}
                            placeholder={field === 'weatherResistance' ? 'Required' : `System (${inherited[field]})`}
                            changed={isChanged(row, field)}
                            error={tuningValueError(field, draft[row]?.[field])}
                            disabled={!canEdit || isSaving}
                            onChange={(value) => setValue(row, field, value)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  )
                })}
                {!visibleTrails.length && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 6 : 5} sx={{ color: 'grey.500' }}>
                      No trails match.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box>
            <Typography variant="caption" color={errorCount ? 'error' : 'grey.500'}>
              {errorCount
                ? `Fix the ${
                    errorCount === 1 ? 'value' : `${errorCount} values`
                  } outlined in red before saving. Hover one to see why.`
                : changes.length
                ? `${changes.length} unsaved ${changes.length === 1 ? 'change' : 'changes'}, highlighted in orange.`
                : 'No unsaved changes.'}
            </Typography>
          </Box>
        </Stack>
      )}
      {detail && (
        <ApplyTuningProfileDialog
          profile={isApplyOpen ? detail.profile : null}
          trailSystemName={trailSystem?.name ?? 'this trail system'}
          activeProfileName={activeProfileName}
          isApplying={isApplying}
          onCancel={() => setIsApplyOpen(false)}
          onApply={handleApply}
        />
      )}
    </Paper>
  )
}
export default TuningProfileEditor
